import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Select,
  Table,
  Spin,
  notification,
  Alert,
} from "antd";
import { IoArrowBack } from "react-icons/io5";
import formatIndianNumber from "../../../../../utils/formatIndianNumber";
import mainContext from "../context/MainContext/mainContext";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

const components = {
  header: {
    cell: (props) => (
      <th
        {...props}
        style={{ ...props.style, backgroundColor: "#daeffe" }}
      />
    ),
  },
};

const ConsolidatedBillingSelect = () => {
  const {
    setConsolidatedMode,
    setConsolidatedBillingIds,
    parties,
    financialYear,
  } = useContext(mainContext);

  const [partyName, setPartyName] = useState(null);
  const [loading, setLoading] = useState(false);
  const [billings, setBillings] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  useEffect(() => {
    if (!partyName) {
      setBillings([]);
      setSelectedRowKeys([]);
      return;
    }

    const fetchBillings = async () => {
      setLoading(true);
      try {
        const fyParam = financialYear
          ? `&financialYear=${encodeURIComponent(financialYear)}`
          : "";
        const response = await fetch(
          `${apiBaseUrl}/inventoryManagement/getBillingsForConsolidation?partyName=${encodeURIComponent(partyName)}${fyParam}`,
          { method: "GET", credentials: "include" }
        );
        const data = await response.json();
        if (response.ok && data.success) {
          setBillings(data.data || []);
          setSelectedRowKeys([]);
        } else {
          notification.error({
            message: data?.message || "Failed to load billings",
          });
          setBillings([]);
        }
      } catch (err) {
        notification.error({ message: err.message });
        setBillings([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBillings();
  }, [partyName, financialYear]);

  const columns = [
    {
      title: "SO No.",
      dataIndex: "stamp",
      key: "stamp",
      align: "center",
    },
    {
      title: "Brand",
      dataIndex: "brandName",
      key: "brandName",
      align: "center",
      render: (v) => (v ? String(v).toUpperCase() : "-"),
    },
    {
      title: "Campaign",
      dataIndex: "campaignName",
      key: "campaignName",
      align: "center",
      render: (v) => (v ? String(v).toUpperCase() : "-"),
    },
    {
      title: "Pending Amount",
      dataIndex: "pendingAmount",
      key: "pendingAmount",
      align: "right",
      render: (v) => `₹ ${formatIndianNumber(v || 0)}`,
    },
  ];

  const handleContinue = () => {
    if (selectedRowKeys.length < 2) {
      notification.warning({
        message: "Select at least two sales orders for one bill.",
      });
      return;
    }
    setConsolidatedBillingIds(selectedRowKeys);
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
        <IoArrowBack
          className="sales-order-arrow-back-icon"
          onClick={() => setConsolidatedMode(false)}
          style={{ cursor: "pointer" }}
        />
        <div style={{ marginLeft: "1rem", fontWeight: 600 }}>
          Create One Bill for Multiple SOs — Select Orders
        </div>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Same party, multiple sales orders — select which orders to include in one bill."
      />

      <div style={{ marginBottom: 16, maxWidth: 400 }}>
        <Select
          placeholder="Select Party"
          showSearch
          allowClear
          optionFilterProp="label"
          style={{ width: "100%" }}
          value={partyName || undefined}
          onChange={(val) => setPartyName(val || null)}
          options={(parties || []).map((name) => ({
            label: name,
            value: name,
          }))}
        />
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table
          rowKey="billingId"
          components={components}
          columns={columns}
          dataSource={billings}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
          pagination={false}
          locale={{
            emptyText: partyName
              ? "No pending billings for this party"
              : "Select a party to view billings",
          }}
        />
      )}

      <div
        style={{
          marginTop: 16,
          display: "flex",
          gap: 12,
          justifyContent: "flex-end",
        }}
      >
        <Button
          type="primary"
          disabled={selectedRowKeys.length < 2}
          onClick={handleContinue}
        >
          Continue ({selectedRowKeys.length} selected)
        </Button>
        <Button onClick={() => setConsolidatedMode(false)}>Cancel</Button>
      </div>
    </div>
  );
};

export default ConsolidatedBillingSelect;
